import {
  ContentItem,
  ContentItems,
  ContentWrapper,
  Space,
  Text,
  TitledText,
} from '@/components';
import { AttachedImageGroup } from '@/features/attachment/_components/AttachedImageGroup';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import {
  type PinpointSheetType,
  getPinpointSceneAndFieldBySheetType,
} from '@/features/comment/utils/pinpoint';
import type { RelatedMaterialModel } from '@/features/project/projects/_models/related-material';
import { useId } from '@/hooks';
import { FALLBACK_TEXT } from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
type Props = {
  images: z.infer<typeof RelatedMaterialModel>['attachments'] | undefined;
  additionalExplanation: z.infer<
    typeof RelatedMaterialModel
  >['additionalExplanation'];
  editable: boolean;
  onClickUpload: () => void;
  pinpointSheetType: PinpointSheetType;
};

export const RelatedMaterialsInformation: FC<Props> = ({
  images,
  additionalExplanation,
  pinpointSheetType,
}) => {
  const { t } = useTranslation();
  const { id } = useId();

  return (
    <ContentWrapper borderBottom={0} paddingTop={0}>
      <ContentItem borderBottom={0} paddingBottom={0}>
        <ContentItems
          items={[
            <PinpointMarker
              key="related-material-image"
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType,
                rs: {
                  field: 'rs.related-material-image',
                  path: { project: id },
                },
                rsSkippedReview: {
                  field: 'rs.skippedReview.related-material-image',
                  path: { project: id },
                },
                ss: {
                  field: 'ss.related-material-image',
                  path: { project: id },
                },
                ksInvestment: {
                  path: { project: id },
                  field: 'ks.investment.related-material-image',
                },
                ksStandard: {
                  path: { project: id },
                  field: 'ks.standard.related-material-image',
                },
                ksLocalGovernment: {
                  path: { project: id },
                  field: 'ks.local-government.related-material-image',
                },
              })}
            >
              <Space size="small" direction="vertical">
                <Text text={t('related-material')} color="secondary" />
                {images?.length ? (
                  <Space size="small" direction="vertical">
                    <AttachedImageGroup
                      attachments={images}
                      alt="related-material-image"
                    />
                  </Space>
                ) : (
                  FALLBACK_TEXT
                )}
              </Space>
            </PinpointMarker>,
            <PinpointMarker
              key="remark"
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType,
                rs: {
                  path: { project: id },
                  field: 'rs.related-material-remark',
                },
                rsSkippedReview: {
                  path: { project: id },
                  field: 'rs.skippedReview.related-material-remark',
                },
                ss: {
                  path: { project: id },
                  field: 'ss.related-material-remark',
                },
                ksInvestment: {
                  path: { project: id },
                  field: 'ks.investment.related-material-remark',
                },
                ksStandard: {
                  path: { project: id },
                  field: 'ks.standard.related-material-remark',
                },
                ksLocalGovernment: {
                  path: { project: id },
                  field: 'ks.local-government.related-material-remark',
                },
              })}
            >
              <TitledText title={t('remark')} text={additionalExplanation} />
            </PinpointMarker>,
          ]}
        />
      </ContentItem>
    </ContentWrapper>
  );
};
